/**
 * WLC utility functions
 *
 * Pure helper functions — no side effects, no state, no React dependencies.
 */

/**
 * Extract the product segment from the current URL path.
 * e.g. /products/wlc/dashboard → 'wlc'
 */
export const getProductFromPath = () => {
  const segments = location.pathname.split('/');
  return segments[2] || '';
};

/**
 * Extract the content slug (third path segment) from the current URL.
 * e.g. /products/wlc/community → 'community'
 */
export const getContentSlugFromPath = () => {
  const segments = location.pathname.split('/');
  return segments[3] || '';
};

/**
 * Build a product-relative path.
 * @param {string} productSlug - The product portion of the URL (e.g. 'wlc')
 * @param {string} path - Sub-path to append (e.g. 'current-week')
 * @returns {string}
 */
export const buildProductPath = (productSlug, path = '') => {
  return `/products/${productSlug}/${path}`;
};

/**
 * Build a display name from user first/last names.
 * Returns "FirstName L" (first letter of last name, capitalized).
 */
export const getDisplayName = (firstName = '', lastName = '') => {
  const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
  return `${firstName} ${lastInitial}`.trim();
};

/**
 * Get auth token from localStorage as a fallback.
 * @returns {string|null}
 */
export const getStoredAuthToken = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('user'));
    return stored?.authToken || null;
  } catch {
    return null;
  }
};

/**
 * Check if a given content slug represents a recipe or article
 * (i.e. should render without the default container).
 */
export const isRecipeOrArticle = (slug = '') => {
  return slug.includes('recipe') || slug.includes('article');
};

/**
 * Merge saved priority order with CMS track list, appending newly added tracks.
 * Introductory track is excluded from sortable priority order.
 */
export const getOrderedTrackSlugs = (trackList = [], trackPriorityOrder = []) => {
  const normalizedTrackList = Array.isArray(trackList) ? trackList : [];
  const normalizedPriorityOrder = Array.isArray(trackPriorityOrder) ? trackPriorityOrder.filter(Boolean) : [];

  const knownTrackSlugs = normalizedTrackList
    .map(trackItem => trackItem?.slug)
    .filter(Boolean)
    .filter(slug => slug !== 'wlc-track-introductory');

  const cleanPriorityOrder = normalizedPriorityOrder.filter(slug => slug !== 'wlc-track-introductory');
  const missingTrackSlugs = knownTrackSlugs.filter(trackSlug => !cleanPriorityOrder.includes(trackSlug));

  return [...cleanPriorityOrder, ...missingTrackSlugs];
};

/**
 * Build a priority order with the selected track first, preserving known order for the remainder.
 */
export const buildTrackPriorityOrder = ({ selectedTrackSlug, trackList = [], trackPriorityOrder = [] }) => {
  const mergedOrder = getOrderedTrackSlugs(trackList, trackPriorityOrder);
  const safeSelectedTrackSlug = selectedTrackSlug || mergedOrder[0];

  if (!safeSelectedTrackSlug) {
    return mergedOrder;
  }

  if (!mergedOrder.includes(safeSelectedTrackSlug)) {
    return [safeSelectedTrackSlug, ...mergedOrder];
  }

  return [safeSelectedTrackSlug, ...mergedOrder.filter(trackSlug => trackSlug !== safeSelectedTrackSlug)];
};
